import { useId } from "react";
import { m } from "framer-motion";
import {
  SIGNATURE_FONT_SIZE,
  SIGNATURE_HEIGHT,
  SIGNATURE_PATHS,
  SIGNATURE_TEXT,
  SIGNATURE_WIDTH,
} from "./signaturePaths";

// The glyph outlines are traced at build time by scripts/generate-signature.js.
// They used to be traced in the browser, which put opentype.js (171KB) and a
// 178KB .otf fetch in front of the LCP element for a result that is the same on
// every load. Re-run that script after changing the text or the face.

export function Signature({
  // Kept as props so the call site still reads as a wordmark rather than a
  // magic component, but the outlines are baked — these must match what the
  // generator produced, so a drifting call site fails loudly in development
  // instead of silently rendering the old name at the wrong size.
  text = SIGNATURE_TEXT,
  fontSize = SIGNATURE_FONT_SIZE,
  color = "#000",
  duration = 1.5,
  delay = 0,
  className,
  inView = false,
  once = true,
  // The traced paths carry no text, so the SVG has no accessible name of its
  // own. Pass ariaLabel to expose one; otherwise it is treated as decorative,
  // which is correct when a visible or sr-only heading already states the name.
  ariaLabel,
  // Extra weight, as a fraction of fontSize, stroked onto the filled glyphs.
  // Kept proportional so it scales with the artwork: a fixed pixel value adds
  // a constant to every stroke, which triples a hairline while barely touching
  // a thick downstroke, and that is what flattens a script's modulation.
  // Anything past ~0.06 starts turning the connectors into slabs.
  weight = 0.035,
}) {
  const maskId = `signature-reveal-${useId().replace(/:/g, "")}`;

  if (process.env.NODE_ENV !== "production") {
    if (text !== SIGNATURE_TEXT || fontSize !== SIGNATURE_FONT_SIZE) {
      console.error(
        `<Signature> was given text="${text}" fontSize=${fontSize}, but the ` +
          `baked outlines are text="${SIGNATURE_TEXT}" fontSize=${SIGNATURE_FONT_SIZE}. ` +
          `Update scripts/generate-signature.js and re-run it.`,
      );
    }
  }

  const variants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: { pathLength: 1, opacity: 1 },
  };

  return (
    <m.svg
      /* width/height/viewBox are the real traced dimensions from the first
         frame. This used to render at a placeholder width of 300 and resize to
         426.57 once tracing finished, which shifted the hero. */
      width={SIGNATURE_WIDTH}
      height={SIGNATURE_HEIGHT}
      viewBox={`0 0 ${SIGNATURE_WIDTH} ${SIGNATURE_HEIGHT}`}
      fill="none"
      className={className}
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      focusable="false"
      initial="hidden"
      whileInView={inView ? "visible" : undefined}
      animate={inView ? undefined : "visible"}
      viewport={{ once }}
      /* No key remount any more: the glyphs exist on the first render, so these
         variants run immediately instead of waiting for opentype.js to finish. */
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
      transition={{ opacity: { duration: 0.45, ease: "easeOut" } }}
    >
      {ariaLabel ? <title>{ariaLabel}</title> : null}
      <defs>
        <mask id={maskId} maskUnits="userSpaceOnUse">
          {SIGNATURE_PATHS.map((d, i) => (
            <m.path
              key={i}
              d={d}
              stroke="white"
              strokeWidth={fontSize * 0.22}
              fill="none"
              variants={variants}
              transition={{
                pathLength: {
                  delay: delay + i * 0.2,
                  duration,
                  ease: "easeInOut",
                },
                opacity: {
                  delay: delay + i * 0.2 + 0.01,
                  duration: 0.01,
                },
              }}
              /* No non-scaling-stroke here: the reveal width must stay
                 proportional to the glyphs, or it over-covers whenever the
                 SVG is scaled down (max-w-full on narrow screens). */
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
        </mask>
      </defs>

      {/* Only the masked fill is drawn. There used to be a second, permanently
          visible 2px outline stroke of every glyph on top of this fill — it was
          the "ink" during the write-on, but it was never retired, so it stayed
          welded around the letterforms and added a constant ~2px to every
          stroke. On a hairline that triples the weight; on a thick downstroke
          it barely registers, which is exactly what flattened the script's
          thick/thin modulation. The mask reveal alone animates the writing. */}
      <g mask={`url(#${maskId})`}>
        {SIGNATURE_PATHS.map((d, i) => (
          <path
            key={i}
            d={d}
            fill={color}
            /* Proportional weight, not a fixed pixel outline — see `weight`.
               The stroke stays well inside the mask's reveal, so it is never
               clipped at the glyph edges. */
            stroke={weight > 0 ? color : undefined}
            strokeWidth={weight > 0 ? fontSize * weight : undefined}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
      </g>
    </m.svg>
  );
}
